"use client";
import { useEffect, useRef } from "react";
import { usePrefersReducedMotion } from "./usePrefersReducedMotion";

interface UseParallaxOptions {
  speed?: number; // fraction of scroll delta applied to the element; default 0.3
}

export const useParallax = ({ speed = 0.3 }: UseParallaxOptions = {}) => {
  const ref = useRef<HTMLDivElement>(null);
  const prefersReducedMotion = usePrefersReducedMotion();

  useEffect(() => {
    const element = ref.current;
    if (!element || prefersReducedMotion) return;

    let rafId: number | null = null;
    const maxOffset = window.innerHeight * speed;

    const update = () => {
      const offset = Math.max(
        -maxOffset,
        Math.min(maxOffset, window.scrollY * speed),
      );
      element.style.transform = `translate3d(0, ${offset}px, 0)`;
      rafId = requestAnimationFrame(update);
    };

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && rafId === null) {
          rafId = requestAnimationFrame(update);
        } else if (!entry.isIntersecting && rafId !== null) {
          cancelAnimationFrame(rafId);
          rafId = null;
        }
      },
      { rootMargin: "50% 0px", threshold: 0 },
    );
    observer.observe(element);

    return () => {
      if (rafId !== null) cancelAnimationFrame(rafId);
      observer.disconnect();
      element.style.transform = "";
    };
  }, [speed, prefersReducedMotion]);

  return { ref };
};
